import * as React from "react";
import { Alert, Box, Snackbar, Stack, Typography } from "@mui/material";
import { submitNonExpress } from "@/services/api/caseApi";
import { Document, NonExpressFormData, Person, prettyTitle, ServiceProvider } from "@/types";
import { Header } from "@/components/atoms/Header";
import { FieldRow, FieldsTable, FieldsTableHeader } from "@/components/molecules/FieldsTable";
import { Section } from "@/Sections";
import { UpdateFormTemplateProc } from "@/templates/FormTemplate";
import { AccountDetails } from "./AccountDetails";
import { Hr } from "@/components/atoms/HR";
import { NEContext } from "@/store/NonExpressState";
import { PersonSummary } from "@/components/organisms";
import { Property } from "@/types/property";
import { DeceasedPersonalDetails } from "@/pages/ExpressForm/Submit/DeceasedPersonalDetails";
import { Accordion } from "@/components/molecules";
import { accountTitle, getAccountCount } from "@/helpers/account-helpers";
import { getPCADataSharingConsent } from "@/components/organisms/PCADataSharingConsent";

export type SubmitProps = {
  readonly caseId: string;
  readonly signature: string | null;
  readonly formCompletionState: Record<Section, boolean>;
  readonly serviceProviders: ReadonlyArray<ServiceProvider>;
  readonly persons: ReadonlyArray<Person>;
  readonly properties: ReadonlyArray<Property>;
  readonly prepareUpdate: (section: Section, form: NonExpressFormData) => NonExpressFormData;
  readonly updateSuccessful: (form: NonExpressFormData, section: Section) => void;
  readonly updateFailure: (error: Error) => void;
  readonly form: NonExpressFormData;
  readonly busy: boolean;
  readonly continueWithoutChanges: (section: Section, doNotAdvance?: boolean) => void;
  readonly remoteError: string | undefined;
  readonly changeNotifierEmailAddress: () => void;
  readonly onSectionClick: (section: string) => void;
  readonly updateTemplate: UpdateFormTemplateProc;
  readonly removeRemoteError: () => void;
  readonly uploadedFileInfo: (id: string) => Promise<Document>;
};

export const Submit: React.FunctionComponent<SubmitProps> = ({
  caseId,
  signature,
  formCompletionState,
  serviceProviders,
  persons,
  properties,
  prepareUpdate,
  updateSuccessful,
  updateFailure,
  form,
  remoteError,
  onSectionClick,
  updateTemplate,
  removeRemoteError,
  uploadedFileInfo,
}) => {
  const {
    state: { bypassKYC },
  } = React.useContext(NEContext);

  const [{ busy, error }, setState] = React.useState({
    busy: false,
    error: undefined,
  } as { busy: boolean; error?: string });

  const onSubmit = React.useCallback(() => {
    if (!isFormSubmittable(formCompletionState, bypassKYC)) {
      setState((s) => ({
        ...s,
        error: "Please verify that all steps on the menu are valid before submitting.",
      }));
      return;
    }

    let futureForm: NonExpressFormData = form;

    futureForm = prepareUpdate(Section.Submit, futureForm);
    setState({ busy: true, error: undefined });
    let formData = {
      ...futureForm,
      case: {
        pca_data_sharing_consent: getPCADataSharingConsent(),
      },
    };
    submitNonExpress({ caseId, signature, form: formData }).then(
      () => {
        setState((s) => ({ ...s, busy: false }));
        updateSuccessful(futureForm, Section.Submit);
      },
      (e) => {
        setState({ busy: false, error: `${e.message}` });
        updateFailure(e);
      }
    );
  }, [
    caseId,
    form,
    prepareUpdate,
    signature,
    updateFailure,
    updateSuccessful,
    formCompletionState,
  ]);

  const removeErrors = React.useCallback(() => {
    removeRemoteError();
    setState({ busy: false });
  }, [removeRemoteError, setState]);

  const onEditNotifier = React.useCallback(() => {
    onSectionClick(Section.Notifier);
  }, [onSectionClick]);

  const onEditDeceased = React.useCallback(() => {
    onSectionClick(Section.Deceased);
  }, [onSectionClick]);

  const onEditAccounts = React.useCallback(() => {
    onSectionClick(Section.Accounts);
  }, [onSectionClick]);

  return (
    <SubmitView
      form={form}
      formCompletionState={formCompletionState}
      error={error}
      busy={busy}
      serviceProviders={serviceProviders || []}
      properties={properties}
      persons={persons}
      remoteError={remoteError}
      onSubmit={onSubmit}
      onSectionClick={onSectionClick}
      updateTemplate={updateTemplate}
      removeRemoteError={removeErrors}
      onEditNotifier={onEditNotifier}
      onEditDeceased={onEditDeceased}
      onEditAccounts={onEditAccounts}
      uploadedFileInfo={uploadedFileInfo}
    />
  );
};

export type SubmitViewProps = {
  readonly form: NonExpressFormData;
  readonly formCompletionState: Record<Section, boolean>;
  readonly error?: string;
  readonly remoteError?: string;
  readonly busy: boolean;
  readonly serviceProviders: ReadonlyArray<ServiceProvider>;
  readonly persons: ReadonlyArray<Person>;
  readonly properties: ReadonlyArray<Property>;

  readonly updateTemplate: UpdateFormTemplateProc;
  readonly removeRemoteError: () => void;
  readonly onSubmit: () => void;
  readonly onSectionClick: (section: string) => void;
  readonly onEditNotifier: () => void;
  readonly onEditDeceased: () => void;
  readonly onEditAccounts: () => void;
  readonly uploadedFileInfo: (id: string) => Promise<Document>;
};

export const SubmitView: React.FC<SubmitViewProps> = ({
  form,
  formCompletionState,
  error,
  remoteError,
  busy,
  serviceProviders,
  persons,
  properties,
  onSubmit,
  onSectionClick,
  updateTemplate,
  removeRemoteError,
  onEditNotifier,
  onEditDeceased,
  onEditAccounts,
  uploadedFileInfo,
}) => {
  React.useEffect(() => {
    updateTemplate({
      busy,
      currentSection: "submit",
      nextLabel: "Submit case",
      onNext: onSubmit,
      onSectionClick,
    });
  }, [busy, onSubmit, onSectionClick, updateTemplate]);

  return (
    <Stack rowGap={4}>
      <Stack rowGap={2}>
        <Header level={1}>Finally, let’s review and submit your case.</Header>

        <Typography variant="body1">
          The information you have provided will be used to notify the companies you selected.
          Please review the information below before submitting your case.
        </Typography>
      </Stack>

      <Hr />

      {form.notifier && formCompletionState[Section.Notifier] && (
        <>
          <PersonSummary person={form.notifier} onEdit={onEditNotifier} />
          <Hr />
        </>
      )}

      {form.deceased && formCompletionState[Section.Deceased] && (
        <DeceasedPersonalDetails
          deceased={form.deceased}
          onEditDeceased={onEditDeceased}
          properties={properties}
        />
      )}

      {!!form.nok && (
        <>
          <PersonSummary person={form.nok} onEdit={() => {}} />
          <Hr />
        </>
      )}
      {!!form.executor && (
        <>
          <PersonSummary person={form.executor} onEdit={() => {}} />
          <Hr />
        </>
      )}

      {form.accounts && (
        <FieldsTableHeader
          title={`Accounts (${getAccountCount(form.accounts)})`}
          onEdit={onEditAccounts}
        />
      )}

      {formCompletionState[Section.Accounts] && (
        <Stack rowGap={2}>
          {(form.accounts || [])
            .filter(
              (account) =>
                !account.additionalServicesAccount &&
                account.serviceProviderType !== "affiliate_partner"
            )
            .map((account, index) => (
              <Accordion key={account.id ?? index} title={accountTitle(account)}>
                <AccountDetails
                  serviceProviders={serviceProviders || []}
                  persons={persons}
                  properties={properties}
                  account={account}
                  uploadedFileInfo={uploadedFileInfo}
                />
              </Accordion>
            ))}
        </Stack>
      )}

      <Typography variant="body1">
        <b>
          {" "}
          By clicking ‘Submit Case’ below, the details you have submitted will be reviewed by a
          member of our team. You will be able to update any of the information provided, including
          adding additional accounts, at a later stage.
        </b>
      </Typography>

      <Snackbar
        sx={{ top: "58px" }}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        open={!!remoteError || !!error}
        autoHideDuration={6000}
        onClose={removeRemoteError}
      >
        <Alert elevation={6} variant="filled" severity="error" onClose={removeRemoteError}>
          {remoteError ?? error}
        </Alert>
      </Snackbar>
    </Stack>
  );
};

const isFormSubmittable = (formCompletionState: Record<Section, boolean>, bypassKYC: boolean) => {
  return (
    formCompletionState[Section.Accounts] &&
    formCompletionState[Section.Documents] &&
    formCompletionState[Section.Notifier] &&
    formCompletionState[Section.Deceased] &&
    (formCompletionState[Section.KYC] || bypassKYC)
  );
};
